import { DestroyRef, Directive, ElementRef, inject, input, OnDestroy, OnInit } from '@angular/core';
import { NgControl } from '@angular/forms';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';

import { distinctUntilChanged, filter, fromEvent, map, merge, Observable, startWith } from 'rxjs';
import { FormControlState, NgrxFormControlValueType } from 'ngrx-forms';

import { KrosFormFieldComponent } from '../kros-form-field/kros-form-field.component';
import { KrosControl } from '../kros-form-field/kros-control.directive';

@Directive({
  selector: 'input[krosEllipsisHint]',
  host: {
    class: 'ellipsis',
  },
  standalone: false
})
export class KrosEllipsisHintDirective implements OnInit, OnDestroy {
  readonly #elementRef = inject(ElementRef);
  readonly #formField = inject(KrosFormFieldComponent);
  readonly #destroyRef = inject(DestroyRef);
  readonly #krosControl = inject(KrosControl, { host: true });
  readonly #ngControl = inject(NgControl, {
    host: true,
    optional: true
  });

  readonly #resizeObserver = new ResizeObserver(() => this.updateHint(this.#krosControl.value));

  ngrxFormControlState = input<FormControlState<NgrxFormControlValueType<any>> | null>(null);
  readonly #ngrxFormControlValue$ = toObservable(this.ngrxFormControlState).pipe(
    filter(Boolean),
    map(state => state.value)
  );

  readonly valueChanges = merge(
    this.#ngrxFormControlValue$,
    this.#ngControl?.valueChanges ?? new Observable(),
    fromEvent(this.#elementRef.nativeElement, 'input').pipe(
      map((e: any) => e.target.value)
    )
  ).pipe(distinctUntilChanged());

  ngOnInit(): void {
    this.valueChanges.pipe(
      startWith(this.#krosControl.value),
      takeUntilDestroyed(this.#destroyRef)
    ).subscribe(value => this.updateHint(value));

    this.#resizeObserver.observe(this.#elementRef.nativeElement);
  }

  ngOnDestroy(): void {
    this.#resizeObserver.disconnect();
  }

  private updateHint(content: string): void {
    const clientWidth = this.#elementRef.nativeElement.clientWidth;
    const scrollWidth = this.#elementRef.nativeElement.scrollWidth;
    const hint = clientWidth < scrollWidth
      ? content
      : '';
    this.#formField.setHint(hint);
  }
}
